import { useMemo } from "react";

import MicIcon from "@mui/icons-material/Mic";
import StopIcon from "@mui/icons-material/Stop";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import useTheme from "@mui/material/styles/useTheme";

import { getScribeModalPopoverZIndex } from "features/medical-scribe/medical-scribe-constants";

import {
  getCompleteTooltipTitle,
  getResumeTooltipTitle,
  isCompleteButtonDisabled,
  isResumeButtonDisabled,
  isResumeButtonPleaseWait
} from "./paused-scribe-action-buttons.util";

type PausedScribeActionButtonsProps = {
  isPaused: boolean;
  isCompleted: boolean;
  isRecordingStatus: boolean;
  isProcessingStatus: boolean;
  canCompleteFromFailedOrCancelled: boolean;
  isFailedOrCancelledAwaitingChunks: boolean;
  isWidgetActive: boolean;
  isLocallyPausedWidget: boolean;
  isResuming: boolean;
  isResumePleaseWait: boolean;
  isCompleting: boolean;
  onResume: () => void;
  onComplete: () => void;
};

export default function PausedScribeActionButtons({
  isPaused,
  isCompleted,
  isRecordingStatus,
  isProcessingStatus,
  canCompleteFromFailedOrCancelled,
  isFailedOrCancelledAwaitingChunks,
  isWidgetActive,
  isLocallyPausedWidget,
  isResuming,
  isResumePleaseWait,
  isCompleting,
  onResume,
  onComplete
}: Readonly<PausedScribeActionButtonsProps>) {
  const theme = useTheme();

  const tooltipPopperProps = useMemo(
    () => ({
      style: { zIndex: getScribeModalPopoverZIndex(theme.zIndex.snackbar) }
    }),
    [theme.zIndex.snackbar]
  );

  const tooltipInput = useMemo(
    () => ({
      isPaused,
      isCompleted,
      isRecordingStatus,
      isProcessingStatus,
      canCompleteFromFailedOrCancelled,
      isFailedOrCancelledAwaitingChunks,
      isWidgetActive,
      isLocallyPausedWidget,
      isResuming,
      isResumePleaseWait,
      isCompleting
    }),
    [
      isPaused,
      isCompleted,
      isRecordingStatus,
      isProcessingStatus,
      canCompleteFromFailedOrCancelled,
      isFailedOrCancelledAwaitingChunks,
      isWidgetActive,
      isLocallyPausedWidget,
      isResuming,
      isResumePleaseWait,
      isCompleting
    ]
  );

  const isCompleteDisabled = isCompleteButtonDisabled(tooltipInput);
  const isResumeDisabled = isResumeButtonDisabled(tooltipInput);
  const showResumePleaseWait = isResumeButtonPleaseWait(tooltipInput);
  const resumeVariant =
    isCompleteDisabled && !isResumeDisabled ? "contained" : "outlined";
  const completeVariant =
    !isCompleteDisabled && !isResumeDisabled ? "contained" : "outlined";

  const resumeTooltip = useMemo(
    () => getResumeTooltipTitle(tooltipInput),
    [tooltipInput]
  );

  const completeTooltip = useMemo(
    () => getCompleteTooltipTitle(tooltipInput),
    [tooltipInput]
  );

  return (
    <Stack direction="row" spacing={1} flexWrap="wrap">
      <Tooltip
        title={resumeTooltip}
        disableInteractive
        PopperProps={tooltipPopperProps}
      >
        <span>
          <Button
            variant={resumeVariant}
            color="primary"
            disabled={isResumeDisabled}
            onClick={onResume}
            startIcon={
              showResumePleaseWait ? (
                <CircularProgress size={14} />
              ) : (
                <MicIcon />
              )
            }
            size="small"
          >
            {showResumePleaseWait ? "Please wait" : "Resume"}
          </Button>
        </span>
      </Tooltip>
      <Tooltip
        title={completeTooltip}
        disableInteractive
        PopperProps={tooltipPopperProps}
      >
        <span>
          <Button
            variant={completeVariant}
            color="primary"
            disabled={isCompleteDisabled}
            onClick={onComplete}
            startIcon={
              isCompleting ? <CircularProgress size={14} /> : <StopIcon />
            }
            size="small"
          >
            {isCompleting ? "Please wait" : "Complete Scribe"}
          </Button>
        </span>
      </Tooltip>
    </Stack>
  );
}
